<template>
  <div
    class="slim-switch-wrap"
    :class="wrapClass"
  >
    <label
      class="slim-switch"
      :for="inputId"
    >
      <input
        :id="inputId"
        v-model="inputValue"
        :value="inputValue"
        type="checkbox"
        :disabled="disabled"
      />
      <span class="thumb" />
    </label>
    <span
      v-if="label"
      class="slim-switch-label"
    >{{ label }}</span>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  activeColor: {
    type: String,
    default: 'green',
    validator: (v) => ['green', 'brand', 'secondary'].includes(v),
  },
});

const emit = defineEmits(['update:model-value']);

const inputId = `slim-switch-${getCurrentInstance()?.uid || 0}`;

const wrapClass = computed(() => ({
  'slim-switch-color-brand': props.activeColor === 'brand',
  'slim-switch-color-secondary': props.activeColor === 'secondary',
}));

const inputValue = computed({
  get: () => props.modelValue,
  set: (val) => {
    emit('update:model-value', val);
  },
});
</script>

<style lang="less" scoped>
.slim-switch-wrap {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.slim-switch-label {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: @text-light;
  white-space: nowrap;
}

.slim-switch {
  position: relative;
  background-color: @grey-700;
  width: 32px;
  height: 16px;
  border-radius: 24px;

  input {
    visibility: hidden;
    max-height: 0;
    max-width: 0;
  }

  .thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: @white;
    transition: all 300ms;
  }

  &:hover {
    cursor: pointer;
  }

  input[value="true"] + span {
    transform: translateX(16px);
  }

  &:has(input[value="true"]) {
    background-color: @basic-green;
  }

  &:has(input[disabled]) {
    filter: opacity(0.4);

    &:hover {
      cursor: default;
    }
  }
}

.slim-switch-wrap:has(.slim-switch input[value="true"]) .slim-switch-label {
  color: @basic-green;
}

.slim-switch-color-brand:has(.slim-switch input[value="true"]) .slim-switch-label {
  color: @text-brand;
}

.slim-switch-color-secondary:has(.slim-switch input[value="true"]) .slim-switch-label {
  color: @brand-secondary-dark;
}

.slim-switch-wrap:has(.slim-switch input[disabled]) .slim-switch-label {
  color: @text-lighter;
}

.slim-switch-color-brand .slim-switch:has(input[value="true"]) {
  background-color: @text-brand;
}

.slim-switch-color-secondary .slim-switch:has(input[value="true"]) {
  background-color: @brand-secondary-dark;
}
</style>
